import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import api from '../../api';
import usePlayerMeta, { normName, abbrevInjury } from './usePlayerMeta';
import { teamLogo, CapMeter, fmtCap, NHL_CAP_MIN, pkey } from './poolFx';
import useStatsMode, { ProfileSlot, StatsHint } from './useStatsMode';

// Free agency: drop one of your players, sign an undrafted one at the same position.
// 2 pickups a month (pool.fa_pickups_per_month). A new player only scores from his signing on.
// Players whose game has already started today can't be dropped or signed.
// A player listed OUT or IR can be replaced for free: no pickup used.
export default function FreeAgents({ pool, userId, lang = 'en', onSwapped }) {
  const fr = lang === 'fr';
  const t = fr ? TR.fr : TR.en;
  const perMonth = pool.fa_pickups_per_month ?? 2;
  const { injuryMap } = usePlayerMeta();
  const injOf = (name) => injuryMap?.get(normName(name || ''));
  const [injuries, setInjuries] = useState({});   // team → the injury report
  const [allPlayers, setAllPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [picks, setPicks] = useState([]);         // every pick in the pool
  const [moves, setMoves] = useState([]);         // pool_transactions
  const [today, setToday] = useState({});
  const [pos, setPos] = useState('F');
  const [search, setSearch] = useState('');
  const [dropId, setDropId] = useState(null);
  const [add, setAdd] = useState(null);
  const stats = useStatsMode();   // hold a row 1 s = that player's profile under his row
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  const load = useCallback(async () => {
    const [{ data: pk }, { data: tx }] = await Promise.all([
      supabase.from('pool_picks').select('*').eq('pool_id', pool.id),
      supabase.from('pool_transactions').select('*').eq('pool_id', pool.id).order('created_at', { ascending: false }),
    ]);
    setPicks(pk || []);
    setMoves(tx || []);
  }, [pool.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const ch = supabase.channel(`pool-fa-${pool.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pool_picks', filter: `pool_id=eq.${pool.id}` }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [pool.id, load]);

  useEffect(() => {
    // keeps refreshing while the server is still building the NHL list (after a restart)
    const stopRosters = api.watchRosters((players, updating) => {
      if (players.length) setAllPlayers(players);
      if (players.length || !updating) setLoadingPlayers(false);
    }, () => setLoadingPlayers(false));
    let alive = true;
    const pull = async () => { try { const d = await api.fetchNhlToday(); if (alive) setToday(d?.teams || {}); } catch (e) { /* no locks */ } };
    pull();
    api.fetchAllInjuries().then((d) => { if (alive) setInjuries(d?.injuries || {}); }).catch(() => {});
    const id = setInterval(pull, 60 * 1000);
    return () => { alive = false; clearInterval(id); stopRosters(); };
  }, []);

  // pickups used this calendar month
  const monthKey = (d) => { const x = new Date(d); return `${x.getFullYear()}-${x.getMonth()}`; };
  const nowKey = monthKey(Date.now());
  const used = moves.filter((m) => m.user_id === userId && m.kind !== 'injury' && monthKey(m.created_at) === nowKey).length;
  const resetDate = (() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 1); })()
    .toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { month: 'long', day: 'numeric' });

  const isLocked = (team) => !!today[team]?.started && !today[team]?.final;   // game being played right now
  const takenIds = useMemo(() => new Set(picks.map((p) => pkey(p.player_id))), [picks]); // by NHL id
  const mine = picks.filter((p) => p.user_id === userId && p.position === pos)
    .sort((a, b) => (a.pick_number ?? 999) - (b.pick_number ?? 999));
  const drop = picks.find((p) => p.player_id === dropId && p.user_id === userId);
  const seasonPts = useMemo(() => Object.fromEntries(allPlayers.map((p) => [pkey(p.id), p.points || 0])), [allPlayers]);

  // ── Salary cap: a pickup must keep you under the cap; over the cap (penalties) you get 1 pickup
  //    that month and it can't raise your payroll ──
  const hasCap = !!pool.cap_mode && pool.cap_amount != null;
  const capById = useMemo(() => new Map(allPlayers.map((p) => [pkey(p.id), p.capHit ?? NHL_CAP_MIN])), [allPlayers]);
  const capOf = (p) => (p.position === 'T' ? 0 : Number(p.cap_hit ?? capById.get(pkey(p.player_id)) ?? NHL_CAP_MIN));
  const payroll = picks.filter((p) => p.user_id === userId).reduce((sum, p) => sum + capOf(p), 0);
  const overCap = hasCap && payroll > pool.cap_amount;
  const monthLimit = overCap ? Math.min(perMonth, 1) : perMonth;
  const left = Math.max(0, monthLimit - used);
  const afterMove = (addPlayer) => payroll - (drop ? capOf(drop) : 0) + (addPlayer.capHit ?? NHL_CAP_MIN);
  const fits = (addPlayer) => {
    if (!hasCap || !drop) return true;
    const next = afterMove(addPlayer);
    return next <= pool.cap_amount || (overCap && next <= payroll);
  };

  // OUT or IR on the player's team today: the same check the server makes
  const teamById = useMemo(() => new Map(allPlayers.map((p) => [pkey(p.id), p.team])), [allPlayers]);
  const isFreeMove = (p) => {
    const list = injuries[teamById.get(pkey(p.player_id)) || p.team] || [];
    const hit = list.find((i) => normName(i.name) === normName(p.player_name || ''));
    return !!hit && abbrevInjury(hit.status) !== 'DTD';
  };
  const freeMove = !!drop && isFreeMove(drop);
  const hasFreeMove = picks.some((p) => p.user_id === userId && p.position !== 'T' && isFreeMove(p));

  const agents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allPlayers
      .filter((p) => p.position === pos && !takenIds.has(pkey(p.id)) && (!q || p.name.toLowerCase().includes(q) || (p.team || '').toLowerCase() === q))
      .sort((a, b) => (b.points || 0) - (a.points || 0));
  }, [allPlayers, pos, takenIds, search]);

  const switchPos = (p) => { setPos(p); setDropId(null); setAdd(null); setError(''); };

  // cap pools: once you pick who to drop, the free agents that fit your cap come first (best first)
  const shownAgents = hasCap && drop ? [...agents].sort((x, y) => Number(fits(y)) - Number(fits(x))) : agents;

  const confirm = async () => {
    if (!drop || !add || busy || !fits(add)) return;
    setBusy(true); setError(''); setDone('');
    let result;
    if (freeMove) {
      const { data: { session } } = await supabase.auth.getSession();
      result = await api.injurySwap(pool.id, drop.player_id, add, session?.access_token).catch(() => ({}));
    } else {
      const { data, error: err } = await supabase.rpc('free_agent_swap', {
        p_user_id: userId, p_pool_id: pool.id,
        p_drop_player_id: drop.player_id,
        p_add_player_id: add.id, p_add_player_name: add.name, p_add_team: add.team, p_add_position: add.position,
      });
      result = err ? { error: err.message } : data;
    }
    setBusy(false);
    if (!result?.success) { setError(result?.error || t.errSwap); return; }
    setDone(t.signed(add.name, drop.player_name));
    setDropId(null); setAdd(null);
    await load();
    onSwapped?.();
  };

  const usernames = useUsernames(pool.id);
  const who = (uid) => (uid === userId ? t.you : usernames[uid] || t.someone);

  const posLabel = { F: t.forwards, D: t.defence, G: t.goalies };
  const noPickups = left === 0;

  return (
    <div className="dr fa-host">
      {/* ── band: pickups left · the move you're building · confirm ── */}
      <div className="fa-band">
        <div className="fa-left">
          <span className="fa-left-num">{left}<small>/{monthLimit}</small></span>
          <span className="fa-left-label">{t.pickupsLeft}<small>{t.resets(resetDate)}</small></span>
        </div>
        <div className="fa-move">
          <span className={`fa-move-slot drop ${drop ? 'set' : ''}`}>
            <small>{t.drop}</small>
            <b>{drop ? drop.player_name : t.pickDrop}</b>
          </span>
          <span className="fa-move-arrow" aria-hidden="true">→</span>
          <span className={`fa-move-slot add ${add ? 'set' : ''}`}>
            <small>{t.sign}</small>
            <b>{add ? add.name : t.pickAdd}</b>
          </span>
        </div>
        <button className="fa-confirm" disabled={!drop || !add || busy || (noPickups && !freeMove) || !fits(add)} onClick={confirm}>
          {busy ? t.signing : freeMove ? t.confirmFree : t.confirm}
        </button>
      </div>
      {hasCap && (
        <div className="fa-cap fa-msg">
          <CapMeter payroll={drop && add ? afterMove(add) : payroll} cap={pool.cap_amount} label={drop && add ? t.afterMove : t.payroll} />
          {overCap && <div className="cap-warn">{t.overCap(fmtCap(payroll - pool.cap_amount))}</div>}
        </div>
      )}
      {error && <div className="pool-form-error fa-msg">{error}</div>}
      {done && !error && <div className="fa-done fa-msg">{done}</div>}
      {noPickups && <div className="fa-note fa-msg">{t.noneLeft(resetDate)}</div>}
      {hasFreeMove && <div className="fa-note fa-msg">{t.freeHint}</div>}

      <StatsHint stats={stats} lang={lang} className="fa-msg" />
      <div data-flip-root className={`fa-body ${stats.statsMode ? 'stats-mode' : ''}`}>
        {/* ── Free agents ── */}
        <div className="fa-panel fa-agents">
          <div className="draft-filters">
            <div className="dr-pos-chips" role="group">
              {['F', 'D', 'G'].map((p) => (
                <button key={p} className={pos === p ? 'active' : ''} onClick={() => switchPos(p)}>{posLabel[p]}</button>
              ))}
            </div>
            <input type="text" className="draft-search" placeholder={t.search} value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="draft-players-list">
            {loadingPlayers && <div className="pools-empty">{t.loading}</div>}
            {!loadingPlayers && shownAgents.slice(0, 150).map((p, i) => {
              const locked = isLocked(p.team);
              const g = today[p.team];
              const noFit = !fits(p);
              const off = locked || (noPickups && !freeMove) || busy || noFit;
              return (
                <div key={p.id} className="sm-rowwrap" data-flip>
                <button
                  className={`draft-player-row dr-prow sm-card ${add?.id === p.id ? 'staged' : ''} ${noFit ? 'over-cap' : ''} ${stats.cls(p.id)}`}
                  disabled={off && !stats.statsMode}
                  title={locked ? t.lockedHint : ''}
                  {...stats.bind(p.id)}
                  onClick={stats.click(p.id, () => setAdd(add?.id === p.id ? null : p))}
                >
                  <span className="dr-prow-rank">{i + 1}</span>
                  <img className="dr-prow-logo" alt="" loading="lazy" src={teamLogo(p.team)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                  <span className="dr-prow-main">
                    <span className="dr-prow-name">{p.name}</span>
                    <span className="dr-prow-meta">
                      <b className="dr-slot-pos">{p.position}</b>
                      <span className="dr-prow-team">{p.team}</span>
                      {injOf(p.name) && <span className="dr-tag inj" title={injOf(p.name)}>{abbrevInjury(injOf(p.name))}</span>}
                      {hasCap && <span className="dr-tag cap">{fmtCap(p.capHit ?? NHL_CAP_MIN)}</span>}
                      {noFit && <span className="dr-tag overcap">{t.noFit}</span>}
                      {g && <span className="dr-tag full">{locked ? t.playing : `${g.home ? t.vs : '@'} ${g.opponent} · ${timeOf(g.startTimeUTC, fr)}`}</span>}
                    </span>
                  </span>
                  <span className="dr-prow-pts"><b>{p.points || 0}</b><small>{t.pts}</small></span>
                </button>
                {/* a greyed-out row can't be tapped, but it can still be held for the profile */}
                {off && !stats.statsMode && <span className={`sm-catch ${stats.cls(p.id)}`} {...stats.bind(p.id)} />}
                <ProfileSlot stats={stats} id={p.id} lang={lang} game={g || null}
                  pick={{ player_id: p.id, player_name: p.name, team: p.team, position: p.position, cap_hit: p.capHit ?? NHL_CAP_MIN }} />
                </div>
              );
            })}
            {!loadingPlayers && agents.length === 0 && <div className="pools-empty">{t.noAgents}</div>}
          </div>
        </div>

        {/* ── My team at this position (pick who to drop) + recent moves ── */}
        <div className="fa-side">
          <div className="fa-panel fa-mine">
            <h4 className="dr-card-title">{t.myPos(posLabel[pos])}</h4>
            <div className="fa-mine-list">
              {mine.map((p) => {
                const locked = isLocked(p.team);
                const free = isFreeMove(p);
                const off = locked || (noPickups && !free) || busy;
                return (
                  <div key={p.player_id} className="sm-rowwrap" data-flip>
                  <button
                    className={`fa-mine-row sm-card ${dropId === p.player_id ? 'selected' : ''} ${stats.cls(p.player_id)}`}
                    disabled={off && !stats.statsMode}
                    title={locked ? t.lockedHint : t.dropHint}
                    {...stats.bind(p.player_id)}
                    onClick={stats.click(p.player_id, () => setDropId(dropId === p.player_id ? null : p.player_id))}
                  >
                    <img alt="" src={teamLogo(p.team)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                    <span className="fa-mine-name">{p.player_name}{injOf(p.player_name) && <span className="dr-tag inj" title={injOf(p.player_name)}>{abbrevInjury(injOf(p.player_name))}</span>}{free && <span className="dr-tag free">{t.freeTag}</span>}</span>
                    <span className="fa-mine-meta">
                      {locked ? t.playing : hasCap ? fmtCap(capOf(p)) : p.acquired_via === 'free_agent' ? t.faTag : `#${p.pick_number ?? '—'}`}
                    </span>
                    <span className="fa-mine-pts">{seasonPts[pkey(p.player_id)] ?? '—'}</span>
                  </button>
                  {off && !stats.statsMode && <span className={`sm-catch ${stats.cls(p.player_id)}`} {...stats.bind(p.player_id)} />}
                  <ProfileSlot stats={stats} id={p.player_id} pick={p} lang={lang} compact game={today[p.team] || null} />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="fa-panel fa-moves">
            <h4 className="dr-card-title">{t.recent}</h4>
            {moves.length === 0 && <div className="fa-moves-empty">{t.noMoves}</div>}
            {moves.slice(0, 5).map((m) => (
              <div key={m.id} className="fa-move-row">
                <span className="fa-move-who">{who(m.user_id)}{m.kind === 'injury' && <span className="dr-tag free">{t.injuryTag}</span>}</span>
                <span className="fa-move-what">
                  <span className="in">+ {m.add_player_name}</span>
                  <span className="out">− {m.drop_player_name}</span>
                </span>
                <span className="fa-move-when">{new Date(m.created_at).toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric' })}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// usernames of the pool members (for the recent moves)
function useUsernames(poolId) {
  const [map, setMap] = useState({});
  useEffect(() => {
    let alive = true;
    supabase.from('pool_members').select('user_id, profiles(username)').eq('pool_id', poolId).then(({ data }) => {
      if (alive) setMap(Object.fromEntries((data || []).map((m) => [m.user_id, m.profiles?.username || '—'])));
    });
    return () => { alive = false; };
  }, [poolId]);
  return map;
}

function timeOf(iso, fr) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString(fr ? 'fr-CA' : 'en-CA', { hour: 'numeric', minute: '2-digit' });
}

const TR = {
  en: {
    pickupsLeft: 'pickups left this month',
    resets: (d) => `Resets ${d}`,
    drop: 'Drop', sign: 'Sign',
    pickDrop: 'Pick one of your players', pickAdd: 'Pick a free agent',
    confirm: 'Confirm pickup', signing: 'Signing…',
    signed: (a, d) => `${a} signed — ${d} is now a free agent. He scores for you from his next game.`,
    noneLeft: (d) => `No pickups left this month. Back on ${d}.`,
    forwards: 'Forwards', defence: 'Defence', goalies: 'Goalies',
    search: 'Search a free agent…', loading: 'Loading players…', noAgents: 'No free agents here',
    pts: 'pts', vs: 'vs', playing: 'playing now',
    lockedHint: 'His game has started — try again after it ends.',
    dropHint: 'Drop this player',
    myPos: (p) => `My ${p.toLowerCase()}`,
    faTag: 'signed', recent: 'Latest moves', noMoves: 'No moves yet this season',
    you: 'You', someone: '—',
    errSwap: 'Could not make that pickup',
    confirmFree: 'Replace for free', freeTag: 'Free swap', injuryTag: 'Injury',
    freeHint: 'Players listed OUT or IR can be replaced for free. It doesn\'t use a pickup.',
    payroll: 'Payroll', afterMove: 'After this move', noFit: 'Over the cap',
    overCap: (by) => `You're ${by} over the cap: only 1 pickup this month, and it can't raise your payroll.`,
  },
  fr: {
    pickupsLeft: 'signatures restantes ce mois-ci',
    resets: (d) => `Remise à zéro le ${d}`,
    drop: 'Libérer', sign: 'Signer',
    pickDrop: 'Choisis un de tes joueurs', pickAdd: 'Choisis un joueur autonome',
    confirm: 'Confirmer', signing: 'Signature…',
    signed: (a, d) => `${a} signé — ${d} est maintenant autonome. Il marque pour toi dès son prochain match.`,
    noneLeft: (d) => `Plus de signatures ce mois-ci. De retour le ${d}.`,
    forwards: 'Attaquants', defence: 'Défenseurs', goalies: 'Gardiens',
    search: 'Chercher un joueur autonome…', loading: 'Chargement des joueurs…', noAgents: 'Aucun joueur autonome ici',
    pts: 'pts', vs: 'vs', playing: 'en jeu',
    lockedHint: 'Son match est commencé — réessaie après le match.',
    dropHint: 'Libérer ce joueur',
    myPos: (p) => `Mes ${p.toLowerCase()}`,
    faTag: 'signé', recent: 'Derniers mouvements', noMoves: 'Aucun mouvement cette saison',
    you: 'Toi', someone: '—',
    errSwap: 'Impossible de faire cette signature',
    confirmFree: 'Remplacer gratuitement', freeTag: 'Gratuit', injuryTag: 'Blessure',
    freeHint: 'Un joueur OUT ou IR peut être remplacé gratuitement. Ça n\'utilise pas de signature.',
    payroll: 'Masse salariale', afterMove: 'Après ce mouvement', noFit: 'Au-dessus du plafond',
    overCap: (by) => `Tu dépasses le plafond de ${by} : 1 seule signature ce mois-ci, et elle ne peut pas augmenter ta masse salariale.`,
  },
};
